import type { StorageSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync } from '../util/safe.js';

/**
 * Доступность хранилищ и приватный режим.
 *
 * Приватный режим сам по себе не признак мошенничества — это законная
 * настройка приватности. Но он объясняет часть аномалий (пустые хранилища,
 * отсутствие истории посещений), и без него правила скоринга наказывали бы
 * пользователя дважды за одно и то же.
 */

const VISIT_KEY = 'fraudeai.visit';

function probeStorage(factory: () => Storage | undefined): boolean {
  return (
    attempt(() => {
      const storage = factory();
      if (!storage) return false;
      const key = '__fraudeai_probe__';
      storage.setItem(key, '1');
      storage.removeItem(key);
      return true;
    }, false) ?? false
  );
}

export const storageModule = defineModule({
  id: 'storage',
  phase: 1,
  timeoutMs: 3000,
  async run(): Promise<StorageSignals> {
    const localStorageOk = probeStorage(() => globalThis.localStorage);
    const sessionStorageOk = probeStorage(() => globalThis.sessionStorage);

    const indexedDbOk =
      (await attemptAsync(
        () =>
          new Promise<boolean>((resolve) => {
            if (!globalThis.indexedDB) return resolve(false);
            const request = indexedDB.open('__fraudeai_probe__', 1);
            request.onsuccess = () => {
              request.result.close();
              attempt(() => indexedDB.deleteDatabase('__fraudeai_probe__'));
              resolve(true);
            };
            request.onerror = () => resolve(false);
            request.onblocked = () => resolve(false);
          }),
        false,
      )) ?? false;

    const cookiesOk =
      attempt(() => {
        document.cookie = '__fraudeai_probe__=1; SameSite=Lax; path=/';
        const present = document.cookie.includes('__fraudeai_probe__');
        document.cookie = '__fraudeai_probe__=; Max-Age=0; path=/';
        return present;
      }, false) ?? false;

    const estimate = await attemptAsync(() => navigator.storage?.estimate());
    const quotaBytes = estimate?.quota ?? null;
    const usageBytes = estimate?.usage ?? null;
    const persisted = await attemptAsync(() => navigator.storage?.persisted());

    // Эвристика приватного режима. В приватном окне браузеры режут квоту
    // до нескольких сотен мегабайт вместо десятков гигабайт.
    let incognitoGuess: boolean | null = null;
    let incognitoReason: string | null = null;

    if (quotaBytes !== null) {
      const quotaMb = quotaBytes / (1024 * 1024);
      if (quotaMb < 300) {
        incognitoGuess = true;
        incognitoReason = `квота хранилища ${Math.round(quotaMb)} МБ — характерно для приватного окна`;
      } else {
        incognitoGuess = false;
        incognitoReason = `квота хранилища ${Math.round(quotaMb)} МБ`;
      }
    } else if (!indexedDbOk && localStorageOk) {
      incognitoGuess = true;
      incognitoReason = 'IndexedDB недоступен при работающем localStorage';
    }

    // Идентификатор предыдущего визита нужен, чтобы отличить возвращающегося
    // пользователя от нового. Записываем его только если хранилище работает.
    let previousVisitId: string | null = null;
    if (localStorageOk) {
      previousVisitId = attempt(() => localStorage.getItem(VISIT_KEY));
    }

    return {
      localStorage: localStorageOk,
      sessionStorage: sessionStorageOk,
      indexedDB: indexedDbOk,
      cookies: cookiesOk,
      openDatabase: 'openDatabase' in globalThis,
      cacheStorage: 'caches' in globalThis,
      quotaBytes,
      usageBytes,
      persisted: persisted ?? null,
      incognitoGuess,
      incognitoReason,
      previousVisitId,
    };
  },
});

/** Сохраняет идентификатор посетителя для распознавания повторного визита. */
export function rememberVisit(visitorId: string): void {
  attempt(() => localStorage.setItem(VISIT_KEY, visitorId));
}
